// src/components/Footer.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRocket,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faFacebook,
  faTwitter,
  faLinkedin,
  faInstagram
} from '@fortawesome/free-solid-svg-icons';
import {
  faFacebook as faFacebookBrand,
  faTwitter as faTwitterBrand,
  faLinkedin as faLinkedinBrand,
  faInstagram as faInstagramBrand
} from '@fortawesome/free-brands-svg-icons';

function Footer() {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Características', path: '/features' },
    { name: 'Acerca de', path: '/about' },
    { name: 'Documentación', path: '/documentation/welcome' }
  ];

  const legalLinks = [
    { name: 'Términos y Condiciones', path: '/terminos' },
    { name: 'Política de Privacidad', path: '/privacy' },
    { name: 'Política de Cookies', path: '/politica-de-cookies' },
    { name: 'Aviso Legal', path: '/legal' }
  ];

  const socialLinks = [
    { icon: faFacebookBrand, name: 'Facebook', url: 'https://facebook.com/petitprime' },
    { icon: faTwitterBrand, name: 'Twitter', url: 'https://twitter.com/petitprime' },
    { icon: faLinkedinBrand, name: 'LinkedIn', url: 'https://linkedin.com/company/petitprime' },
    { icon: faInstagramBrand, name: 'Instagram', url: 'https://instagram.com/petitprime' }
  ];

  return (
    <footer className="bg-gray-900 text-white relative z-30">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-6">
              <img
                src="/logos/logopng.png"
                alt="PetitPrime Logo"
                className="h-10 w-auto"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                PetitPrime
              </span>
            </div>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Transforma tu negocio con la plataforma de fidelización digital más completa para pymes.
              Aumenta ventas y retención de clientes con programas de lealtad inteligentes.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors duration-300"
                  aria-label={social.name}
                >
                  <FontAwesomeIcon icon={social.icon} className="text-lg" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Enlaces Rápidos</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-blue-400 transition-colors duration-300 flex items-center"
                  >
                    <FontAwesomeIcon icon={faRocket} className="mr-2 text-xs" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Legal</h3>
            <ul className="space-y-3">
              {legalLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-blue-400 transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Contacto</h3>
            <div className="space-y-4">
              <div className="flex items-center text-gray-300">
                <FontAwesomeIcon icon={faEnvelope} className="mr-3 text-blue-400" />
                <a
                  href="mailto:info@petitstores.com"
                  className="hover:text-blue-400 transition-colors duration-300"
                >
                  info@petitstores.com
                </a>
              </div>
              <div className="flex items-start text-gray-300">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-3 mt-1 text-blue-400" />
                <span>Perú, Chile, España</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © {currentYear} PetitPrime. Todos los derechos reservados.
            </div>
            <div className="flex items-center space-x-6 text-sm">
              <span className="text-gray-400">Hecho con ❤️ en España</span>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-green-400">Sistema Activo</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110 z-50"
        aria-label="Volver arriba"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </footer>
  );
}

export default Footer;
